/**
 * Service API pour la gestion des cas de fraude
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export interface CasFraude {
  id: number;
  reference: string;
  etudiant: number;
  etudiant_nom: string;
  etudiant_matricule: string;
  etudiant_classe: string;
  matiere_nom: string;
  matiere_code: string;
  annee_academique: string;
  semestre: string;
  type_fraude: string;
  type_fraude_display: string;
  gravite: string;
  gravite_display: string;
  date_detection: string;
  date_examen: string | null;
  description: string;
  circonstances: string;
  code_copie_1: string;
  code_copie_2: string;
  pieces_jointes: string;
  statut: string;
  statut_display: string;
  sanction: string;
  sanction_display: string | null;
  sanction_details: string;
  date_sanction: string | null;
  decision_conseil: string;
  detecte_par: number | null;
  detecte_par_nom: string | null;
  traite_par: number | null;
  traite_par_nom: string | null;
  date_creation: string;
  date_modification: string;
  notes_internes: string;
  historique: Array<{
    id: number;
    ancien_statut: string;
    nouveau_statut: string;
    commentaire: string;
    effectue_par_nom: string | null;
    date_action: string;
  }>;
  preuves: PreuveFraude[];
  nb_preuves: number;
}

export interface PreuveFraude {
  id: number;
  cas_fraude: number;
  fichier: string;
  fichier_url: string;
  type_preuve: string;
  type_preuve_display: string;
  titre: string;
  description: string;
  ajoute_par: number | null;
  ajoute_par_nom: string | null;
  date_ajout: string;
}

export interface StatistiquesFraudes {
  total: number;
  en_cours: number;
  sanctionnes: number;
  clos: number;
  par_type: Array<{ type_fraude: string; count: number }>;
  par_statut: Array<{ statut: string; count: number }>;
  par_gravite: Array<{ gravite: string; count: number }>;
}

export interface VerificationFraude {
  has_fraude: boolean;
  count: number;
  cas_actifs: CasFraude[];
  total_historique: number;
}

export const fraudesApi = createApi({
  reducerPath: 'fraudesApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['Fraudes'],
  endpoints: (builder) => ({
    getFraudes: builder.query<CasFraude[], Record<string, any> | void>({
      query: (params) => ({
        url: '/fraudes/',
        params: params ?? undefined,
      }),
      transformResponse: (response: any) => {
        if (response && response.results) return response.results;
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['Fraudes'],
    }),

    getFraudeById: builder.query<CasFraude, number>({
      query: (id) => `/fraudes/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Fraudes', id }],
    }),

    createFraude: builder.mutation<CasFraude, Partial<CasFraude>>({
      query: (data) => ({
        url: '/fraudes/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Fraudes'],
    }),

    updateFraude: builder.mutation<CasFraude, { id: number; data: Partial<CasFraude> }>({
      query: ({ id, data }) => ({
        url: `/fraudes/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Fraudes'],
    }),

    deleteFraude: builder.mutation<void, number>({
      query: (id) => ({
        url: `/fraudes/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Fraudes'],
    }),

    changerStatutFraude: builder.mutation<CasFraude, { id: number; statut: string; commentaire?: string; sanction?: string; sanction_details?: string }>({
      query: ({ id, ...data }) => ({
        url: `/fraudes/${id}/changer_statut/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Fraudes'],
    }),

    getStatistiquesFraudes: builder.query<StatistiquesFraudes, { annee_academique?: string } | void>({
      query: (params) => ({
        url: '/fraudes/statistiques/',
        params: params ?? undefined,
      }),
      providesTags: ['Fraudes'],
    }),

    verifierEtudiantFraude: builder.query<VerificationFraude, { etudiant_id?: number; matricule?: string }>({
      query: (params) => ({
        url: '/fraudes/verifier_etudiant/',
        params,
      }),
      providesTags: ['Fraudes'],
    }),

    ajouterPreuveFraude: builder.mutation<PreuveFraude, { id: number; formData: FormData }>({
      query: ({ id, formData }) => ({
        url: `/fraudes/${id}/ajouter_preuve/`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Fraudes'],
    }),

    supprimerPreuveFraude: builder.mutation<{ success: boolean; message: string }, { fraudeId: number; preuveId: number }>({
      query: ({ fraudeId, preuveId }) => ({
        url: `/fraudes/${fraudeId}/supprimer_preuve/${preuveId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Fraudes'],
    }),
  }),
});

export const {
  useGetFraudesQuery,
  useGetFraudeByIdQuery,
  useCreateFraudeMutation,
  useUpdateFraudeMutation,
  useDeleteFraudeMutation,
  useChangerStatutFraudeMutation,
  useGetStatistiquesFraudesQuery,
  useVerifierEtudiantFraudeQuery,
  useAjouterPreuveFraudeMutation,
  useSupprimerPreuveFraudeMutation,
} = fraudesApi;
